import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { FrameAuthService } from './frame-auth.service';
import { GameService } from './game.service';

export const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const game = inject(GameService);
  const frameAuth = inject(FrameAuthService);
  const authed = attachBearer(req, game, frameAuth);

  return next(authed).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (
        !frameAuth.isIframe ||
        req.context.get(AUTH_RETRIED) ||
        !frameAuth.isTrustedApiRequest(req.url)
      ) {
        return throwError(() => err);
      }

      const stale = bearerOf(authed) || game.token;
      return frameAuth.waitForFreshAccess(stale).pipe(
        switchMap((token) => {
          if (!token) {
            return throwError(() => err);
          }
          const retry = attachBearer(
            req.clone({ context: req.context.set(AUTH_RETRIED, true) }),
            game,
            frameAuth,
            token
          );
          return next(retry);
        })
      );
    })
  );
};

function attachBearer(
  req: Parameters<HttpInterceptorFn>[0],
  game: GameService,
  frameAuth: FrameAuthService,
  token = game.token
) {
  if (!token || !frameAuth.isTrustedApiRequest(req.url)) {
    return req;
  }
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

function bearerOf(req: Parameters<HttpInterceptorFn>[0]): string {
  const header = req.headers.get('Authorization');
  if (!header) {
    return '';
  }
  return header.replace(/^Bearer\s+/i, '').trim();
}
